<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { onMount, getContext } from 'svelte';
    import { Dropdown } from '@oat-sa-private/ui-elements';
    import { getInteractionStateStore } from '../../itemsStateStore.js';
    import { extractFromClasses } from '../../interactions/util/attributes.js';
    import { findClosestBlockParent } from '../../interactions/util/dom.js';

    const qtiClass = 'qti-inlineChoiceInteraction';

    let {
        // keys for state store:
        itemIdentifier,
        responseIdentifier,

        required = false,

        choices = [],

        // inherited item-level QTI attributes:
        language,
        id,
        dir,
        classes = '',

        // inherited aria and data attributes:
        role,
        ariaAttrs = {},
        dataAttrs = {}
    } = $props();

    // svelte-ignore non_reactive_update
    let choiceOptions, placeholder, inputWidth;

    setDerivedProps();

    function setDerivedProps() {
        choiceOptions = choices;
        placeholder = dataAttrs['data-prompt'] || '';
        inputWidth = extractFromClasses(classes, 'qti-input-width-', val => parseInt(val, 10));
    }

    // svelte-ignore state_referenced_locally
    const interactionStateStore = getInteractionStateStore(itemIdentifier, responseIdentifier);

    let wrapperElement;

    // load the response when the store change
    let selected = $state('');
    interactionStateStore.subscribe(() => {
        const response = interactionStateStore.getResponseValue();
        if (response) {
            selected = loadResponse();
        }
    });

    // svelte-ignore state_referenced_locally
    const itemContext = getContext(itemIdentifier);
    const writingMode = itemContext && itemContext.getWritingMode();

    /**
     * Load the interaction response
     * @returns {String} the identifier of the selected response
     */
    function loadResponse() {
        // if value is invalid, do not load it
        if (interactionStateStore.getValidity() === false) {
            return selected;
        }
        let storedValue = interactionStateStore.getResponseValue();

        if (typeof storedValue === 'undefined' || storedValue === null) {
            storedValue = '';
        }
        return storedValue;
    }

    onMount(() => {
        //we need to expand the line height of the closest parent block of the inline choice
        const parentElement = findClosestBlockParent(wrapperElement);
        if (parentElement) {
            parentElement.classList.add('inline-interaction-container');
        }
    });
</script>

<style>
    :global(.inline-interaction-container) {
        line-height: 2.5;
    }
    .qti-inlineChoiceInteraction {
        margin: 0 1rem;
        line-height: 1.5;

        /* answered style - thick border is done with increased box shadow to avoid height jump */
        &.selected :global(button) {
            @add-mixin double-border var(--color-border-actionable), var(--border-thin), var(--border-medium-plus);
            &:hover {
                @add-mixin double-border var(--color-border-actionable-hover), var(--border-thin),
                    var(--border-medium-plus);
            }
        }
        /* layout values inherited from item runner, and passed down to Dropdown component */
        --dropdown-total-max-height: var(--item-container-block-size);
        --dropdown-viewport-offset-top: var(--item-container-offset-block-start);
    }

    :global([data-layouts~='hideFeedbacksLayout']) {
        & .qti-inlineChoiceInteraction :global(.feedback-inline) {
            display: none;
        }
    }
</style>

<span
    class="qti-interaction qti-inlineInteraction {qtiClass} {classes}"
    class:selected
    bind:this={wrapperElement}
    lang={language}
    {id}
    {dir}
    {role}
    {...ariaAttrs}
    {...dataAttrs}>
    <Dropdown
        options={choiceOptions}
        bind:value={selected}
        disabled={true}
        {placeholder}
        height="small"
        size={inputWidth}
        selectFocused={false}
        {required}
        visibleOptions={0}
        {writingMode} />
</span>
